<template>
  <div class="mx-6 flex flex-1">
    <BaseFlexSpinner v-if="loading || isLoading" />
    <div
      v-else
      class="flex flex-1 flex-col"
    >
      <SupportProgramTabShortcuts
        :program="program"
        :support-program="supportProgram"
      />
      <SupportProgramStatistics
        :support-program="supportProgram"
        :market-statistics="marketStatistics"
        :settings="settings"
        :refreshing="refreshing"
        :refreshed-at="refreshedAt"
        :cached-at="supportProgram.meta?.cached_at"
        @update:group-settings="updateGroupSetting"
        @refresh="refreshStatistics"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, ref, watch} from "vue";
import {programApi} from '@/container';
import {
    Program,
    SupportProgram,
    SupportProgramMarketStatistic,
    SupportProgramSettings
} from "@/program/interfaces/Program";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import SupportProgramTabShortcuts from "@/program/components/support-program/SupportProgramTabShortcuts.vue";
import SupportProgramStatistics from "@/program/components/support-program/SupportProgramStatistics.vue";
import useSupportProgramSettings from "@/program/composables/useSupportProgramSettings";
import {useToastStore} from "@/stores/toast.store";

const {program} = defineProps<{ program: Program }>();

const supportProgram = ref<SupportProgram>({} as SupportProgram);
const marketStatistics = ref<SupportProgramMarketStatistic[]>([]);
const loading = ref<boolean>(true);
const refreshing = ref<boolean>(false);
const refreshedAt = ref<number | null>(null);

const {
    data: settings,
    isLoading,
    updateGroup
} = useSupportProgramSettings(program.programmable_id, {dedupingInterval: 10000});

/**
 * On component mounted, load support program and statistics.
 */
onMounted(() => {
    Promise.all([
        readSupportProgram(program.programmable_id),
        readMarketStatistics(program.programmable_id)
    ]).finally(() => {
        loading.value = false;
    });
});

/**
 * Read a support program.
 */
const readSupportProgram = async (id: number) => {
    return programApi()
        .supportProgram(id)
        .then((_supportProgram) => {
            supportProgram.value = _supportProgram;
        });
};

/**
 * Read a support program market statistics.
 */
const readMarketStatistics = async (id: number, fromCache: boolean = true) => {
    return programApi()
        .statistics(id, fromCache)
        .then((responseStats) => {
            marketStatistics.value = responseStats;
        });
};

const updateGroupSetting = async (slug: string, enabled: boolean) => {
    await updateGroup(slug, enabled);
};

/**
 * Recompute the program data then read it back, keeping the statistics
 * displayed during the call: the refreshing flag drives the progress bar,
 * never the page spinner. A throttled recompute (429) still reads back, as
 * the data was just computed anyway.
 */
const refreshStatistics = async (): Promise<void> => {
    if (refreshing.value) {
        return;
    }

    refreshing.value = true;

    try {
        await programApi()
            .refreshConsumers(program.programmable_id)
            .catch((error) => {
                if ((error as { response?: { status?: number } }).response?.status !== 429) {
                    throw error;
                }
            });

        await Promise.all([
            readSupportProgram(program.programmable_id),
            readMarketStatistics(program.programmable_id, false)
        ]);

        refreshedAt.value = Date.now();
    } catch {
        useToastStore().showError({
            content: 'Impossible d\'actualiser les données. Vérifiez votre connexion et réessayez.'
        });
    } finally {
        refreshing.value = false;
    }
};

/**
 * Watch for settings change, if optional groups have changed, reload the support program to get fresh stats.
 */
watch(() => settings.value, (newValue: SupportProgramSettings, oldValue: SupportProgramSettings | undefined) => {
    if (!oldValue) {
        return;
    }

    const optionalGroups = newValue?.meta?.optional_groups ?? [];

    if (!optionalGroups.length) {
        return;
    }

    const optionalNewSettings = newValue.groups
        .filter((groupSetting) => optionalGroups.includes(groupSetting.group));

    const optionalOldSettings = oldValue?.groups
        .filter((groupSetting) => optionalGroups.includes(groupSetting.group));

    const hasChanged = optionalNewSettings.some((newSetting) => {
        const oldSetting = optionalOldSettings?.find((oldSetting) => oldSetting.group === newSetting.group);
        return oldSetting?.enabled !== newSetting.enabled;
    });

    if (hasChanged) {
        Promise.all([
            readSupportProgram(program.programmable_id),
            readMarketStatistics(program.programmable_id, false)
        ]);
    }
}, {deep: true});
</script>
